import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Plus, Edit, DollarSign, Layers, BarChart3, TrendingUp } from 'lucide-react';
import { RecordActions } from '../../../components/common/RecordActions';
import { RecordViewDialog } from '../../../components/common/RecordViewDialog';
import { useAuth } from '../../../context/AuthContext';
import { hcmAPI } from '../../../services/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Card } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { toast } from 'sonner';

const PayScaleConfig = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('payscaleconfig');
  const [viewItem, setViewItem] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  const getActiveTabFromPath = () => {
    const path = location.pathname;
    if (path.includes('/pay-scale-areas')) return 'pay-scale-areas';
    if (path.includes('/pay-scale-groups')) return 'pay-scale-groups';
    if (path.includes('/pay-scale-levels')) return 'pay-scale-levels';
    return 'pay-scale-types';
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromPath());
  const [payScaleTypes, setPayScaleTypes] = useState([]);
  const [payScaleAreas, setPayScaleAreas] = useState([]);
  const [payScaleGroups, setPayScaleGroups] = useState([]);
  const [payScaleLevels, setPayScaleLevels] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formResetCounter, setFormResetCounter] = useState(0);
  
  // Form state
  const [selectedPayScaleType, setSelectedPayScaleType] = useState('');
  const [selectedPayScaleArea, setSelectedPayScaleArea] = useState('');
  const [selectedPayScaleGroup, setSelectedPayScaleGroup] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Active');
  const [selectedCurrency, setSelectedCurrency] = useState('INR');

  const tabs = [
    { id: 'pay-scale-types', label: 'Pay Scale Types', icon: DollarSign },
    { id: 'pay-scale-areas', label: 'Pay Scale Areas', icon: Layers },
    { id: 'pay-scale-groups', label: 'Pay Scale Groups', icon: BarChart3 },
    { id: 'pay-scale-levels', label: 'Pay Scale Levels', icon: TrendingUp },
  ];

  useEffect(() => {
    loadData();
  // eslint-disable-next-line
  }, [activeTab]);

  useEffect(() => {
    const newTab = getActiveTabFromPath();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  // eslint-disable-next-line
  }, [location.pathname]);

  const loadData = async () => {
    try {
      if (activeTab === 'pay-scale-types') {
        const res = await hcmAPI.getPayScaleTypes();
        setPayScaleTypes(res.data);
      } else if (activeTab === 'pay-scale-areas') {
        const [areasRes, typesRes] = await Promise.all([
          hcmAPI.getPayScaleAreas(),
          hcmAPI.getPayScaleTypes()
        ]);
        setPayScaleAreas(areasRes.data);
        setPayScaleTypes(typesRes.data);
      } else if (activeTab === 'pay-scale-groups') {
        const [groupsRes, typesRes, areasRes] = await Promise.all([
          hcmAPI.getPayScaleGroups(),
          hcmAPI.getPayScaleTypes(),
          hcmAPI.getPayScaleAreas()
        ]);
        setPayScaleGroups(groupsRes.data);
        setPayScaleTypes(typesRes.data);
        setPayScaleAreas(areasRes.data);
      } else if (activeTab === 'pay-scale-levels') {
        const [levelsRes, typesRes, areasRes, groupsRes] = await Promise.all([
          hcmAPI.getPayScaleLevels(),
          hcmAPI.getPayScaleTypes(),
          hcmAPI.getPayScaleAreas(),
          hcmAPI.getPayScaleGroups()
        ]);
        setPayScaleLevels(levelsRes.data);
        setPayScaleTypes(typesRes.data);
        setPayScaleAreas(areasRes.data);
        setPayScaleGroups(groupsRes.data);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/hcm/config/pay-scale/${tabId}`);
  };

  const resetFormState = () => {
    setSelectedPayScaleType('');
    setSelectedPayScaleArea('');
    setSelectedPayScaleGroup('');
    setSelectedStatus('Active');
    setSelectedCurrency('INR');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Clean up empty strings
    Object.keys(data).forEach(key => {
      if (data[key] === '' || data[key] === null || data[key] === undefined) {
        if (['description', 'region', 'job_grade'].includes(key)) {
          delete data[key];
        }
      }
    });

    try {
      if (activeTab === 'pay-scale-types') {
        if (editingItem) {
          await hcmAPI.updatePayScaleType(editingItem.pay_scale_type_id, data);
          toast.success('Pay Scale Type updated successfully');
        } else {
          await hcmAPI.createPayScaleType(data);
          toast.success('Pay Scale Type created successfully');
        }
      } else if (activeTab === 'pay-scale-areas') {
        if (editingItem) {
          await hcmAPI.updatePayScaleArea(editingItem.pay_scale_type_id, editingItem.pay_scale_area_id, data);
          toast.success('Pay Scale Area updated successfully');
        } else {
          await hcmAPI.createPayScaleArea(data);
          toast.success('Pay Scale Area created successfully');
        }
      } else if (activeTab === 'pay-scale-groups') {
        if (editingItem) {
          await hcmAPI.updatePayScaleGroup(editingItem.pay_scale_type_id, editingItem.pay_scale_area_id, editingItem.pay_scale_group_id, data);
          toast.success('Pay Scale Group updated successfully');
        } else {
          await hcmAPI.createPayScaleGroup(data);
          toast.success('Pay Scale Group created successfully');
        }
      } else if (activeTab === 'pay-scale-levels') {
        // Convert salary fields to numbers
        if (data.min_salary) data.min_salary = parseFloat(data.min_salary);
        if (data.mid_salary) data.mid_salary = parseFloat(data.mid_salary);
        if (data.max_salary) data.max_salary = parseFloat(data.max_salary);
        if (editingItem) {
          await hcmAPI.updatePayScaleLevel(editingItem.pay_scale_type_id, editingItem.pay_scale_area_id, editingItem.pay_scale_group_id, editingItem.pay_scale_level_id, data);
          toast.success('Pay Scale Level updated successfully');
        } else {
          await hcmAPI.createPayScaleLevel(data);
          toast.success('Pay Scale Level created successfully');
        }
      }
      
      setIsDialogOpen(false);
      setEditingItem(null);
      resetFormState();
      setFormResetCounter(prev => prev + 1);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Operation failed');
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    
    if (activeTab === 'pay-scale-types') {
      setSelectedStatus(item.status || 'Active');
    } else if (activeTab === 'pay-scale-areas') {
      setSelectedPayScaleType(item.pay_scale_type_id || '');
      setSelectedStatus(item.status || 'Active');
    } else if (activeTab === 'pay-scale-groups') {
      setSelectedPayScaleType(item.pay_scale_type_id || '');
      setSelectedPayScaleArea(item.pay_scale_area_id || '');
      setSelectedStatus(item.status || 'Active');
    } else if (activeTab === 'pay-scale-levels') {
      setSelectedPayScaleType(item.pay_scale_type_id || '');
      setSelectedPayScaleArea(item.pay_scale_area_id || '');
      setSelectedPayScaleGroup(item.pay_scale_group_id || '');
      setSelectedStatus(item.status || 'Active');
      setSelectedCurrency(item.currency || 'INR');
    }
    
    setIsDialogOpen(true);
    setFormResetCounter(prev => prev + 1);
  };

  const getFormFields = () => {
    if (activeTab === 'pay-scale-types') {
      return (
        <>
          <div>
            <Label htmlFor="pay_scale_type_id">Pay Scale Type ID *</Label>
            <Input id="pay_scale_type_id" name="pay_scale_type_id" placeholder="e.g., PST01" maxLength={10} defaultValue={editingItem?.pay_scale_type_id || ''} disabled={!!editingItem} required />
          </div>
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input id="name" name="name" placeholder="e.g., Union Pay Scale" defaultValue={editingItem?.name || ''} required />
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Input id="description" name="description" placeholder="Description" defaultValue={editingItem?.description || ''} />
          </div>
          <div>
            <Label htmlFor="country">Country</Label>
            <Input id="country" name="country" placeholder="e.g., IN" defaultValue={editingItem?.country || 'IN'} maxLength={3} />
          </div>
          <div>
            <Label htmlFor="status">Status *</Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
            <input type="hidden" name="status" value={selectedStatus} />
          </div>
        </>
      );
    } else if (activeTab === 'pay-scale-areas') {
      return (
        <>
          <div>
            <Label htmlFor="pay_scale_type_id">Pay Scale Type *</Label>
            <Select value={selectedPayScaleType} onValueChange={setSelectedPayScaleType} disabled={!!editingItem}>
              <SelectTrigger><SelectValue placeholder="Select Type" /></SelectTrigger>
              <SelectContent>
                {payScaleTypes.filter(t => t.status === 'Active').map(t => (
                  <SelectItem key={t.pay_scale_type_id} value={t.pay_scale_type_id}>{t.pay_scale_type_id} - {t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" name="pay_scale_type_id" value={selectedPayScaleType} />
          </div>
          <div>
            <Label htmlFor="pay_scale_area_id">Pay Scale Area ID *</Label>
            <Input id="pay_scale_area_id" name="pay_scale_area_id" placeholder="e.g., PSA01" maxLength={10} defaultValue={editingItem?.pay_scale_area_id || ''} disabled={!!editingItem} required />
          </div>
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input id="name" name="name" placeholder="e.g., North Region" defaultValue={editingItem?.name || ''} required />
          </div>
          <div>
            <Label htmlFor="region">Region</Label>
            <Input id="region" name="region" placeholder="e.g., North, South" defaultValue={editingItem?.region || ''} />
          </div>
          <div>
            <Label htmlFor="status">Status</Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
            <input type="hidden" name="status" value={selectedStatus} />
          </div>
        </>
      );
    } else if (activeTab === 'pay-scale-groups') {
      return (
        <>
          <div>
            <Label htmlFor="pay_scale_type_id">Pay Scale Type *</Label>
            <Select value={selectedPayScaleType} onValueChange={setSelectedPayScaleType} disabled={!!editingItem}>
              <SelectTrigger><SelectValue placeholder="Select Type" /></SelectTrigger>
              <SelectContent>
                {payScaleTypes.filter(t => t.status === 'Active').map(t => (
                  <SelectItem key={t.pay_scale_type_id} value={t.pay_scale_type_id}>{t.pay_scale_type_id} - {t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" name="pay_scale_type_id" value={selectedPayScaleType} />
          </div>
          <div>
            <Label htmlFor="pay_scale_area_id">Pay Scale Area *</Label>
            <Select value={selectedPayScaleArea} onValueChange={setSelectedPayScaleArea} disabled={!!editingItem}>
              <SelectTrigger><SelectValue placeholder="Select Area" /></SelectTrigger>
              <SelectContent>
                {payScaleAreas.filter(a => a.status === 'Active' && (!selectedPayScaleType || a.pay_scale_type_id === selectedPayScaleType)).map(a => (
                  <SelectItem key={`${a.pay_scale_type_id}-${a.pay_scale_area_id}`} value={a.pay_scale_area_id}>{a.pay_scale_area_id} - {a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" name="pay_scale_area_id" value={selectedPayScaleArea} />
          </div>
          <div>
            <Label htmlFor="pay_scale_group_id">Pay Scale Group ID *</Label>
            <Input id="pay_scale_group_id" name="pay_scale_group_id" placeholder="e.g., G01" maxLength={10} defaultValue={editingItem?.pay_scale_group_id || ''} disabled={!!editingItem} required />
          </div>
          <div>
            <Label htmlFor="name">Name (Grade) *</Label>
            <Input id="name" name="name" placeholder="e.g., Grade A" defaultValue={editingItem?.name || ''} required />
          </div>
          <div>
            <Label htmlFor="job_grade">Job Grade</Label>
            <Input id="job_grade" name="job_grade" placeholder="e.g., Senior, Junior" defaultValue={editingItem?.job_grade || ''} />
          </div>
          <div>
            <Label htmlFor="status">Status</Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
            <input type="hidden" name="status" value={selectedStatus} />
          </div>
        </>
      );
    } else if (activeTab === 'pay-scale-levels') {
      return (
        <>
          <div>
            <Label htmlFor="pay_scale_type_id">Pay Scale Type *</Label>
            <Select value={selectedPayScaleType} onValueChange={setSelectedPayScaleType} disabled={!!editingItem}>
              <SelectTrigger><SelectValue placeholder="Select Type" /></SelectTrigger>
              <SelectContent>
                {payScaleTypes.filter(t => t.status === 'Active').map(t => (
                  <SelectItem key={t.pay_scale_type_id} value={t.pay_scale_type_id}>{t.pay_scale_type_id} - {t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" name="pay_scale_type_id" value={selectedPayScaleType} />
          </div>
          <div>
            <Label htmlFor="pay_scale_area_id">Pay Scale Area *</Label>
            <Select value={selectedPayScaleArea} onValueChange={setSelectedPayScaleArea} disabled={!!editingItem}>
              <SelectTrigger><SelectValue placeholder="Select Area" /></SelectTrigger>
              <SelectContent>
                {payScaleAreas.filter(a => a.status === 'Active' && (!selectedPayScaleType || a.pay_scale_type_id === selectedPayScaleType)).map(a => (
                  <SelectItem key={`${a.pay_scale_type_id}-${a.pay_scale_area_id}`} value={a.pay_scale_area_id}>{a.pay_scale_area_id} - {a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" name="pay_scale_area_id" value={selectedPayScaleArea} />
          </div>
          <div>
            <Label htmlFor="pay_scale_group_id">Pay Scale Group *</Label>
            <Select value={selectedPayScaleGroup} onValueChange={setSelectedPayScaleGroup} disabled={!!editingItem}>
              <SelectTrigger><SelectValue placeholder="Select Group" /></SelectTrigger>
              <SelectContent>
                {payScaleGroups.filter(g => g.status === 'Active' && (!selectedPayScaleType || g.pay_scale_type_id === selectedPayScaleType) && (!selectedPayScaleArea || g.pay_scale_area_id === selectedPayScaleArea)).map(g => (
                  <SelectItem key={`${g.pay_scale_type_id}-${g.pay_scale_area_id}-${g.pay_scale_group_id}`} value={g.pay_scale_group_id}>{g.pay_scale_group_id} - {g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" name="pay_scale_group_id" value={selectedPayScaleGroup} />
          </div>
          <div>
            <Label htmlFor="pay_scale_level_id">Level ID *</Label>
            <Input id="pay_scale_level_id" name="pay_scale_level_id" placeholder="e.g., L1, L2" maxLength={10} defaultValue={editingItem?.pay_scale_level_id || ''} disabled={!!editingItem} required />
          </div>
          <div>
            <Label htmlFor="name">Level Name *</Label>
            <Input id="name" name="name" placeholder="e.g., Entry Level" defaultValue={editingItem?.name || ''} required />
          </div>
          <div>
            <Label htmlFor="min_salary">Min Salary</Label>
            <Input type="number" id="min_salary" name="min_salary" placeholder="30000" defaultValue={editingItem?.min_salary || ''} />
          </div>
          <div>
            <Label htmlFor="mid_salary">Mid Salary</Label>
            <Input type="number" id="mid_salary" name="mid_salary" placeholder="45000" defaultValue={editingItem?.mid_salary || ''} />
          </div>
          <div>
            <Label htmlFor="max_salary">Max Salary</Label>
            <Input type="number" id="max_salary" name="max_salary" placeholder="60000" defaultValue={editingItem?.max_salary || ''} />
          </div>
          <div>
            <Label htmlFor="currency">Currency</Label>
            <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
              <SelectTrigger><SelectValue placeholder="Select Currency" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="INR">INR</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
              </SelectContent>
            </Select>
            <input type="hidden" name="currency" value={selectedCurrency} />
          </div>
          <div>
            <Label htmlFor="status">Status</Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
            <input type="hidden" name="status" value={selectedStatus} />
          </div>
        </>
      );
    }
  };

  const getTableContent = () => {
    let data, headers, rowRenderer;

    if (activeTab === 'pay-scale-types') {
      data = payScaleTypes;
      headers = ['Type ID', 'Name', 'Country', 'Status', 'Actions'];
      rowRenderer = (item) => (
        <tr key={item.pay_scale_type_id} className="border-b border-gray-100 hover:bg-gray-50">
          <td className="py-3 px-4 font-mono text-sm font-semibold">{item.pay_scale_type_id}</td>
          <td className="py-3 px-4">
            <div className="text-sm font-medium">{item.name}</div>
            {item.description && <div className="text-xs text-gray-600">{item.description}</div>}
          </td>
          <td className="py-3 px-4 font-mono text-sm">{item.country || '-'}</td>
          <td className="py-3 px-4">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${item.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {item.status}
            </span>
          </td>
          <td className="py-3 px-4">
            <RecordActions doctype="payscaleconfig" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
          </td>
        </tr>
      );
    } else if (activeTab === 'pay-scale-areas') {
      data = payScaleAreas;
      headers = ['Type ID', 'Area ID', 'Name', 'Region', 'Status', 'Actions'];
      rowRenderer = (item) => (
        <tr key={`${item.pay_scale_type_id}-${item.pay_scale_area_id}`} className="border-b border-gray-100 hover:bg-gray-50">
          <td className="py-3 px-4 font-mono text-sm">{item.pay_scale_type_id}</td>
          <td className="py-3 px-4 font-mono text-sm font-semibold">{item.pay_scale_area_id}</td>
          <td className="py-3 px-4 text-sm font-medium">{item.name}</td>
          <td className="py-3 px-4 text-sm">{item.region || '-'}</td>
          <td className="py-3 px-4">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${item.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {item.status}
            </span>
          </td>
          <td className="py-3 px-4">
            <RecordActions doctype="payscaleconfig" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
          </td>
        </tr>
      );
    } else if (activeTab === 'pay-scale-groups') {
      data = payScaleGroups;
      headers = ['Type', 'Area', 'Group ID', 'Name (Grade)', 'Status', 'Actions'];
      rowRenderer = (item) => (
        <tr key={`${item.pay_scale_type_id}-${item.pay_scale_area_id}-${item.pay_scale_group_id}`} className="border-b border-gray-100 hover:bg-gray-50">
          <td className="py-3 px-4 font-mono text-sm">{item.pay_scale_type_id}</td>
          <td className="py-3 px-4 font-mono text-sm">{item.pay_scale_area_id}</td>
          <td className="py-3 px-4 font-mono text-sm font-semibold">{item.pay_scale_group_id}</td>
          <td className="py-3 px-4">
            <div className="text-sm font-medium">{item.name}</div>
            {item.job_grade && <div className="text-xs text-gray-600">Grade: {item.job_grade}</div>}
          </td>
          <td className="py-3 px-4">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${item.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {item.status}
            </span>
          </td>
          <td className="py-3 px-4">
            <RecordActions doctype="payscaleconfig" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
          </td>
        </tr>
      );
    } else if (activeTab === 'pay-scale-levels') {
      data = payScaleLevels;
      headers = ['Group', 'Level', 'Name', 'Min Salary', 'Mid Salary', 'Max Salary', 'Status', 'Actions'];
      rowRenderer = (item) => (
        <tr key={`${item.pay_scale_type_id}-${item.pay_scale_area_id}-${item.pay_scale_group_id}-${item.pay_scale_level_id}`} className="border-b border-gray-100 hover:bg-gray-50">
          <td className="py-3 px-4 font-mono text-sm">{item.pay_scale_group_id}</td>
          <td className="py-3 px-4 font-mono text-sm font-semibold">{item.pay_scale_level_id}</td>
          <td className="py-3 px-4 text-sm font-medium">{item.name}</td>
          <td className="py-3 px-4 text-sm">{item.min_salary ? `${item.currency} ${item.min_salary.toLocaleString()}` : '-'}</td>
          <td className="py-3 px-4 text-sm">{item.mid_salary ? `${item.currency} ${item.mid_salary.toLocaleString()}` : '-'}</td>
          <td className="py-3 px-4 text-sm">{item.max_salary ? `${item.currency} ${item.max_salary.toLocaleString()}` : '-'}</td>
          <td className="py-3 px-4">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${item.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {item.status}
            </span>
          </td>
          <td className="py-3 px-4">
            <RecordActions doctype="payscaleconfig" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
          </td>
        </tr>
      );
    }

    return { data, headers, rowRenderer };
  };

  const { data, headers, rowRenderer } = getTableContent();

  const getDialogTitle = () => {
    const action = editingItem ? 'Edit' : 'Create';
    const entity = activeTab === 'pay-scale-types' ? 'Pay Scale Type' :
                   activeTab === 'pay-scale-areas' ? 'Pay Scale Area' :
                   activeTab === 'pay-scale-groups' ? 'Pay Scale Group' : 'Pay Scale Level';
    return `${action} ${entity}`;
  };

  return (
    <Layout>
      <div className="p-6" data-testid="pay-scale-config-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm/config/pay-scale')}>Configuration</span>
            <span>/</span>
            <span>Pay Scale</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Pay Scale Configuration</h1>
          <p className="text-gray-600 mt-1">Manage pay scale types, areas, groups, and levels</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Card */}
        <Card className="p-0 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
            <h2 className="font-semibold text-gray-900">
              {tabs.find(t => t.id === activeTab)?.label}
            </h2>
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) {
                setEditingItem(null);
                resetFormState();
              }
            }}>
              <DialogTrigger asChild>
                {writable && (<Button size="sm" data-testid="create-btn">
                  <Plus className="h-4 w-4 mr-1" />
                  Create
                </Button>)}
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{getDialogTitle()}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} key={formResetCounter} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    {getFormFields()}
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-btn">{editingItem ? 'Update' : 'Create'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {headers?.map((header, idx) => (
                    <th key={idx} className="text-left py-3 px-4 text-sm font-semibold text-gray-700">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data?.length > 0 ? (
                  data.map(rowRenderer)
                ) : (
                  <tr>
                    <td colSpan={headers?.length || 1} className="py-8 text-center text-gray-500">
                      No records found. Click "Create" to add a new entry.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default PayScaleConfig;
